// El asistente operativo · AO5 · DIRECTRIZ-ASISTENTE-OPERATIVO
//
// Una conversación: pregunta, respuesta con sus cifras y su «dato al», y
// debajo de cada respuesta qué herramientas se usaron (para comprobar).
// Cada respuesta lleva su marca de honestidad —«Respuesta con IA» o
// «Respuesta rápida»— sin una palabra técnica de por medio (B6). La
// conversación reciente viaja con cada pregunta (coherencia), y el
// historial propio se carga al abrir.
import { useCallback, useEffect, useRef, useState } from 'react';
import { MessageCircleQuestion, Send, WifiOff } from 'lucide-react';
import { api } from '../api';
import Ayuda from '../ui/Ayuda';

const SUGERENCIAS = [
  '¿Qué está bajo mínimo?',
  '¿Cuánto hay de …?',
  '¿Dónde está …?',
  '¿Qué salió de … esta semana?',
];

function hora(iso) {
  try { return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
}

export default function AsistenteOperativo({ irA }) {
  const [estado, setEstado] = useState(null);
  const [resueltas, setResueltas] = useState({});
  const [charla, setCharla] = useState([]);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState(null);
  const fin = useRef(null);

  const cargar = useCallback(() => {
    Promise.all([api.get('/asistente/estado'), api.get('/asistente/historial?limite=10')])
      .then(([e, h]) => {
        setEstado(e);
        setCharla(h.reverse().map((x) => ({ ...x, previa: true })));
      })
      .catch((e) => setError(e.message || 'No se pudo abrir el asistente.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { fin.current?.scrollIntoView?.({ block: 'end' }); }, [charla]);

  async function preguntar(p) {
    const pregunta = (p ?? texto).trim();
    if (ocupado) return;
    // «Preguntar» sin pregunta dice qué falta en vez de quedarse gris o
    // mudo (auditoría de botones, 19 sep 2026; Doctrina 2, regla 8).
    if (!pregunta) { setError('Escribe tu pregunta primero. Por ejemplo: ¿cuánto hay de TR-0412 y dónde?'); return; }
    setOcupado(true); setError(null); setTexto('');
    const historial = charla.slice(-3).map((x) => ({ pregunta: x.pregunta, respuesta: x.respuesta }));
    try {
      const r = await api.post('/asistente/preguntar', { pregunta, historial });
      setCharla((c) => [...c, { pregunta, ...r, creado_en: new Date().toISOString() }]);
      if (r.disponible === false && r.modo !== 'basico') setEstado((e) => ({ ...(e || {}), disponible: false, mensaje: r.respuesta }));
    } catch (e) { setError(e.message || 'No se pudo preguntar.'); }
    finally { setOcupado(false); }
  }

  // Confirmar es una SEGUNDA petición, la única que escribe (el asistente de
  // siempre ya lo hacía así): el servidor revalida entero lo que propuso.
  async function confirmar(i, propuesta) {
    setResueltas((r) => ({ ...r, [i]: 'haciendo' }));
    try {
      await api.post('/bot/ejecutar', { accion: propuesta.accion, argumentos: propuesta.argumentos });
      setResueltas((r) => ({ ...r, [i]: 'hecha' }));
    } catch (e) { setResueltas((r) => ({ ...r, [i]: e.message || 'No se pudo hacer. Inténtalo otra vez.' })); }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Asistente</h1>
        <p className="text-sm text-gray-600">Pregúntale a TEGO dónde está algo, cuánto queda, qué se movió o qué hay que reponer. Escribe como hablas. Cada cifra lleva su fecha; solo escribe lo que tú confirmes.</p>
      </div>
      <Ayuda id="asistente.pantalla" />
      {estado && estado.disponible === false && (
        <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">
            {estado.mensaje || 'El asistente no está disponible ahora.'}
            {/* EXPERIENCIA E2 (parche del integrador en el merge): un aviso sin botón deja a la persona recargando. */}
            <button type="button" onClick={cargar} className="mt-2 block rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900">
              Volver a comprobar
            </button>
          </span>
        </div>
      )}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-sm text-red-700">{error}</p>}

      <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4">
        {charla.length === 0 && (
          <div className="text-sm text-gray-600">
            <p className="flex items-center gap-2 font-semibold text-gray-800"><MessageCircleQuestion className="h-4 w-4" /> Sin preguntas en esta conversación.</p>
            <p className="mt-1">Prueba con una de estas:</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUGERENCIAS.map((s) => (
                <button key={s} type="button" onClick={() => (s.includes('…') ? setTexto(s.replace('…', '')) : preguntar(s))}
                  className="rounded-full border border-gray-200 px-3 py-1 text-sm">{s}</button>
              ))}
            </div>
          </div>
        )}
        {charla.map((x, i) => (
          <div key={x.id || i} className="space-y-1">
            <p className="text-sm text-gray-500"><span className="font-semibold text-gray-700">Tú</span> · {hora(x.creado_en)}: {x.pregunta}</p>
            {/* «Respuesta rápida» (reglas) NO es una avería: se pinta en
                gris como cualquier otra. Solo lo que de verdad falla
                —sin respuesta o error— va en ámbar. */}
            <div className={`rounded-lg border p-3 text-sm ${x.estado === 'NO_DISPONIBLE' || x.estado === 'ERROR'
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : x.estado === 'ACLARACION' ? 'border-blue-200 bg-blue-50 text-blue-900'
              : 'border-gray-200 bg-gray-50 text-gray-900'}`}>
              <p className="whitespace-pre-wrap">{x.respuesta}</p>
              {/* B6 · la marca de honestidad: con qué se armó la respuesta,
                  en palabras humanas, pequeña y en gris. Nunca «modo
                  básico» ni «el modelo local no contesta». */}
              {x.marca && <p className="mt-1 text-[11px] font-medium text-gray-400">{x.marca}</p>}
              {/* R-AO1: un solo asistente. Las acciones (a dónde ir), las
                  preguntas de seguimiento y la propuesta que hay que
                  confirmar vienen del asistente de siempre y se pintan aquí. */}
              {x.acciones && x.acciones.length > 0 && irA && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {x.acciones.map((a) => (
                    <button key={`${a.modulo}:${a.etiqueta}`} type="button" onClick={() => irA(a.modulo, a.contexto || {})}
                      className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800">
                      {a.etiqueta}
                    </button>
                  ))}
                </div>
              )}
              {x.propuesta && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                  {resueltas[i] === 'hecha' ? <span className="text-green-700">Hecho.</span>
                    : resueltas[i] === 'haciendo' ? <span className="text-gray-600">Haciendo…</span>
                    : resueltas[i] ? <span className="text-red-700">{resueltas[i]}</span>
                    : (
                      <button type="button" onClick={() => confirmar(i, x.propuesta)}
                        className="rounded-lg bg-[var(--color-marca)] px-3 py-1.5 text-sm font-semibold text-white">
                        Sí, hazlo
                      </button>
                    )}
                </div>
              )}
              {x.seguimiento && x.seguimiento.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {x.seguimiento.map((sug) => (
                    <button key={sug} type="button" onClick={() => preguntar(sug)} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700">{sug}</button>
                  ))}
                </div>
              )}
              {x.herramientas && x.herramientas.length > 0 && (
                <p className="mt-2 text-[11px] text-gray-500">
                  Consultado con: {x.herramientas.map((h) => h.herramienta).join(', ')}{x.ms ? ` · ${x.ms} ms` : ''}
                </p>
              )}
            </div>
          </div>
        ))}
        <div ref={fin} />
      </div>

      {/* P12 (29 sep 2026): la conversación se guarda y vuelve al abrir, así
          que las preguntas de ejemplo desaparecían para siempre tras la
          primera pregunta (el censo lo midió: «desapareció al volver a abrir
          la pantalla»). Siguen a la mano, pegadas al campo. */}
      {charla.length > 0 && (
        <div role="group" aria-label="Otras preguntas de ejemplo" className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
          <span>Otras preguntas:</span>
          {SUGERENCIAS.map((s) => (
            <button key={s} type="button" onClick={() => (s.includes('…') ? setTexto(s.replace('…', '')) : preguntar(s))}
              className="rounded-full border border-gray-200 px-3 py-1 text-sm">{s}</button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); preguntar(); }} className="flex gap-2">
        <label className="flex-1">
          <span className="sr-only">Tu pregunta</span>
          <input value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={500}
            placeholder="Por ejemplo: ¿cuánto hay de TR-0412 y dónde?"
            className="w-full rounded-lg border border-gray-300 p-3 text-base" />
        </label>
        <button type="submit" disabled={ocupado}
          className="inline-flex items-center gap-1 rounded-lg bg-[var(--color-marca)] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
          <Send className="h-4 w-4" /> {ocupado ? 'Consultando…' : 'Preguntar'}
        </button>
      </form>
      <Ayuda id="asistente.pregunta" />
    </div>
  );
}
